import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithRetry } from "../utils/api/APIHelper";
import { AuthHelper } from "../utils/api/AuthHelper";
import {
  AUTH_FAILURE_WINDOW_MS,
  IMapSas,
  mapSasApiUrl,
  MAX_CONSECUTIVE_AUTH_FAILURES,
  SAS_REFRESH_BUFFER_MS,
  SAS_RETRY_INTERVAL_MS,
} from "../utils/maps/MapConstants";
import { getAzureMapSasUrl } from "../utils/maps/MapHelperFunctions";

interface UseMapSasResult {
  sas: IMapSas | undefined;
  isLoading: boolean;
  error: string | undefined;
  /** Forces an immediate re-fetch of the signature, e.g. after a tile failure. */
  forceRefresh: () => void;
  /** True once tiles keep failing despite fresh sigs, so the caller should remount the tile layers. */
  needsReset: boolean;
  /** Call after handling `needsReset` to resume normal retries. */
  acknowledgeReset: () => void;
}

// Avoid hammering the SAS endpoint when a burst of tiles fail around the same time.
const MIN_FORCE_REFRESH_INTERVAL_MS = 5000;

/**
 * Fetches (and keeps fresh) the short-lived `sig`/`expires` pair used to
 * authorize map tile requests, so tiles don't need a JWT/preflight per request.
 *
 * The signature is fetched once per airport, then proactively refreshed
 * `SAS_REFRESH_BUFFER_MS` before it expires. `forceRefresh` is exposed so a
 * failed tile request (401/403) can trigger an immediate refresh as a fallback.
 */
export const useMapSas = (airportCode: string): UseMapSasResult => {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [sas, setSas] = useState<IMapSas>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [needsReset, setNeedsReset] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const isMountedRef = useRef(true);
  const lastFetchStartedAtRef = useRef(0);
  const consecutiveAuthFailuresRef = useRef(0);
  const lastAuthFailureAtRef = useRef(0);

  const fetchSas = useCallback(
    (code: string): Promise<void> => {
      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      const run = (async () => {
        try {
          lastFetchStartedAtRef.current = Date.now();
          setIsLoading(true);

          const accessToken = await AuthHelper.getAccessToken(msalInstance);
          if (accessToken === "") {
            throw new Error("Access token cannot be empty!");
          }

          if (!process.env.REACT_APP_APIM_KEY) {
            throw new Error("API key is not defined!");
          }

          const headers = {
            "x-api-key": process.env.REACT_APP_APIM_KEY,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json;charset=UTF-8",
          };

          const url = getAzureMapSasUrl(code, mapSasApiUrl);
          const resp = await fetchWithRetry(
            url,
            { mode: "cors", headers },
            2,
            300
          );
          const data: IMapSas = await resp.json();

          if (!isMountedRef.current) {
            return;
          }

          setSas(data);
          setError(undefined);

          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          const msUntilExpiry =
            new Date(data.expires).getTime() -
            Date.now() -
            SAS_REFRESH_BUFFER_MS;

          timerRef.current = setTimeout(
            () => fetchSas(code),
            Math.max(msUntilExpiry, 0)
          );
        } catch (e: any) {
          if (isMountedRef.current) {
            setError(e?.message || "Failed to fetch map signature");

            // The tile layer only mounts once a sig exists, so it can never
            // trigger onAuthError/forceRefresh to retry a fetch that never
            // succeeded in the first place - retry on our own instead.
            if (timerRef.current) {
              clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(
              () => fetchSas(code),
              SAS_RETRY_INTERVAL_MS
            );
          }
        } finally {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
          inFlightRef.current = null;
        }
      })();

      inFlightRef.current = run;
      return run;
    },
    [msalInstance]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (airportCode) {
      fetchSas(airportCode);
    }

    return () => {
      isMountedRef.current = false;
      inFlightRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [airportCode, fetchSas]);

  const forceRefresh = useCallback(() => {
    // Already fetching, or we just fetched very recently (e.g. several tiles
    // failing at once) — let the in-flight/just-finished request stand instead
    // of firing a duplicate SAS call.
    if (
      inFlightRef.current ||
      Date.now() - lastFetchStartedAtRef.current < MIN_FORCE_REFRESH_INTERVAL_MS
    ) {
      return;
    }

    // A tile keeps failing even right after a successful sig fetch, so
    // refreshing the sig again isn't going to fix it - stop and let the
    // caller remount the tile layers instead of hammering the SAS endpoint forever.
    const now = Date.now();
    if (now - lastAuthFailureAtRef.current > AUTH_FAILURE_WINDOW_MS) {
      consecutiveAuthFailuresRef.current = 0;
    }
    lastAuthFailureAtRef.current = now;
    consecutiveAuthFailuresRef.current += 1;

    if (consecutiveAuthFailuresRef.current > MAX_CONSECUTIVE_AUTH_FAILURES) {
      setNeedsReset(true);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (airportCode) {
      fetchSas(airportCode);
    }
  }, [airportCode, fetchSas]);

  const acknowledgeReset = useCallback(() => {
    consecutiveAuthFailuresRef.current = 0;
    lastAuthFailureAtRef.current = 0;
    setNeedsReset(false);
  }, []);

  return {
    sas,
    isLoading,
    error,
    forceRefresh,
    needsReset,
    acknowledgeReset,
  };
};
